import type { QueryClient } from '@tanstack/react-query'
import type { AnalyticsFilters } from './useAnalyticsFilters'

/**
 * Cold analytics queries run 13s+, so data stays fresh for 30 min and cached for an hour:
 * navigating away and back shows it instantly. The Refresh button is the way to force new
 * numbers. Ref: ASC-10670
 */
export const ANALYTICS_STALE_TIME_MS = 30 * 60 * 1000
export const ANALYTICS_GC_TIME_MS = 60 * 60 * 1000

/**
 * Cache key for one analytics section. `filters.refresh` is deliberately NOT part of it: a
 * refresh must land in the same entry every later visit reads, otherwise the fresh result is
 * stored under a throwaway key and the next render shows the stale entry again.
 */
export const ANALYTICS_QUERY_ROOT = 'platform-analytics'
// Position of the endpoint inside `analyticsQueryKey`; `isSameAnalyticsEndpoint` reads it.
const ENDPOINT_INDEX = 1

export function analyticsQueryKey(
  endpoint: string,
  filters: AnalyticsFilters,
): readonly (string | null)[] {
  return [
    ANALYTICS_QUERY_ROOT,
    endpoint,
    filters.dateFrom,
    filters.dateTo,
    filters.agentIds.join(','),
    filters.workspaceId ?? null,
    filters.granularity,
  ]
}

export function isAnalyticsQueryKey(key: readonly unknown[]): boolean {
  return key[0] === ANALYTICS_QUERY_ROOT
}

/**
 * Whether the data cached under `previous` may stand in while `next` loads: any other filter
 * set for the SAME section (endpoint). The old numbers stay on screen, dimmed under the
 * loading banner, until the new response lands, so a slow query never blanks the page.
 * Ref: ASC-10749
 */
export function isSameAnalyticsEndpoint(
  previous: readonly unknown[] | undefined,
  next: readonly unknown[],
): boolean {
  if (!previous || !isAnalyticsQueryKey(previous) || !isAnalyticsQueryKey(next)) return false
  return previous[ENDPOINT_INDEX] === next[ENDPOINT_INDEX]
}

export function buildQueryString(filters: AnalyticsFilters): string {
  const params = new URLSearchParams()
  params.set('dateFrom', filters.dateFrom)
  params.set('dateTo', filters.dateTo)
  if (filters.agentIds.length > 0) params.set('agentIds', filters.agentIds.join(','))
  if (filters.workspaceId) params.set('workspaceId', filters.workspaceId)
  if (filters.refresh) params.set('refresh', 'true')
  params.set('granularity', filters.granularity)
  return params.toString()
}

export interface PrefetchTask<E extends string = string> {
  endpoint: E
  filters: AnalyticsFilters
}

/**
 * Everything worth warming so picking another agent renders instantly, in the order it is
 * most likely to be wanted: "All agents" first (when an agent is open), then one agent at a
 * time across all its endpoints. The view already on screen is fetching itself, so its agent
 * is skipped. `refresh` is never carried over: a warm-up must not bypass the gateway cache.
 */
export function buildAgentPrefetchPlan<E extends string>(
  agentIds: readonly string[],
  filters: AnalyticsFilters,
  endpoints: readonly E[],
): PrefetchTask<E>[] {
  const open = filters.agentIds.join(',')
  const targets: string[][] = []
  if (open !== '') targets.push([])
  for (const id of agentIds) {
    if (id !== open) targets.push([id])
  }
  return targets.flatMap((target) =>
    endpoints.map((endpoint) => ({
      endpoint,
      filters: { ...filters, agentIds: target, refresh: undefined },
    })),
  )
}

/** Runs `run` over `items` with at most `limit` in flight; stops picking up work once cancelled. */
export async function runWithConcurrency<T>(
  items: readonly T[],
  limit: number,
  run: (item: T) => Promise<unknown>,
  isCancelled: () => boolean,
): Promise<void> {
  let next = 0
  const worker = async (): Promise<void> => {
    while (!isCancelled()) {
      const index = next
      next += 1
      if (index >= items.length) return
      await run(items[index])
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
}

/**
 * Runs `start` once no analytics request is in flight, or after `maxWaitMs` regardless, unless
 * `isSuperseded` turns true first. Idleness is only judged after `armAfterMs`, so the visible
 * sections have mounted and started their own fetches by then. Ref: ASC-10749
 */
export function whenAnalyticsIdle(
  queryClient: QueryClient,
  isSuperseded: () => boolean,
  start: () => void,
  timing: { armAfterMs: number; maxWaitMs: number },
): void {
  let settled = false
  let armed = false
  const timers: ReturnType<typeof setTimeout>[] = []
  let unsubscribe: () => void = () => undefined

  // True only for the call that actually settles, so `start` can never run twice.
  const settle = (): boolean => {
    if (settled) return false
    settled = true
    unsubscribe()
    timers.forEach(clearTimeout)
    return true
  }
  const check = (): void => {
    if (settled) return
    if (isSuperseded()) {
      settle()
      return
    }
    if (armed && queryClient.isFetching({ queryKey: [ANALYTICS_QUERY_ROOT] }) === 0 && settle()) {
      start()
    }
  }

  unsubscribe = queryClient.getQueryCache().subscribe(check)
  timers.push(
    setTimeout(() => {
      if (settle() && !isSuperseded()) start()
    }, timing.maxWaitMs),
    setTimeout(() => {
      armed = true
      check()
    }, timing.armAfterMs),
  )
}
